const PHOTO = 'data-sd-preload';
const SLIDE = 'data-sd-slide-src';
const SRCSET = 'data-sd-srcset';
const SELECTOR = 'img[' + PHOTO + '], img[' + SLIDE + ']';
const INSTALLATION = '__sdDeferredImages';
const MARGIN = 300;

// Фото внутри .slider загружает сам слайдер (lib/slider.js): текущий, соседние и следующий слайд.
const inSlider = image => Boolean(image.closest('.slider'));

// Can start in <head>. Discover parser-added nodes without waiting for the app.
export function installDeferredImages(doc) {
    if (doc[INSTALLATION]) return doc[INSTALLATION];
    const win = doc.defaultView;
    let pending = [];
    let stopped = false;
    let observer;
    let mutations;
    const inView = new WeakSet();
    const widths = new WeakMap();

    function measurements() {
        const styles = new Map();
        const visibility = new Map();
        let banner;
        let checkedBanner = false;
        function style(element) {
            if (!styles.has(element)) styles.set(element, win.getComputedStyle(element));
            return styles.get(element);
        }
        function displayed(element) {
            if (!doc.documentElement.contains(element)) return false;
            const visited = [];
            let visible = true;
            for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
                if (visibility.has(node)) {
                    visible = visibility.get(node);
                    break;
                }
                visited.push(node);
                const value = style(node);
                if (node.hidden || value.display === 'none' || value.visibility === 'hidden' || value.visibility === 'collapse') {
                    visible = false;
                    break;
                }
            }
            visited.forEach(node => visibility.set(node, visible));
            return visible;
        }
        function near(element, margin = MARGIN) {
            const rect = element.getBoundingClientRect();
            return rect.bottom > rect.top && rect.right > rect.left && rect.bottom >= -margin &&
                rect.top <= (win.innerHeight || doc.documentElement.clientHeight) + margin &&
                rect.right >= -margin && rect.left <= (win.innerWidth || doc.documentElement.clientWidth) + margin;
        }
        function covered(element) {
            if (!checkedBanner) {
                banner = doc.getElementById('hideAppsFullSizeBanner');
                if (banner && !(displayed(banner) && style(banner).opacity !== '0' && near(banner, 0))) banner = null;
                checkedBanner = true;
            }
            return banner && !banner.contains(element);
        }
        // Never retain layout results across events: a banner close
        // must use the current DOM synchronously.
        return { displayed, covered };
    }

    function load(image, attribute) {
        const url = image.getAttribute(attribute);
        if (!url || url.indexOf('[[') !== -1) return false;
        // Native lazy loading must not delay a slide that is about to move into view.
        if (attribute === SLIDE) image.removeAttribute('loading');
        const srcset = attribute === PHOTO && image.getAttribute(SRCSET);
        if (srcset) {
            // Ширину фото сообщил наблюдатель: браузер выберет версию по ней и плотности экрана.
            // Без ширины srcset считает фото во весь экран и берёт большую версию, как раньше.
            const width = widths.get(image);
            if (width) image.setAttribute('sizes', Math.ceil(width) + 'px');
            image.setAttribute('srcset', srcset);
            image.removeAttribute(SRCSET);
        }
        image.setAttribute('src', url);
        image.removeAttribute(attribute);
        return true;
    }

    // С IntersectionObserver близость к экрану сообщает наблюдатель (inView). Изменения DOM и
    // события окна не читают геометрию фото: каждое такое чтение заставляло браузер заново
    // считать вёрстку страницы на каждой пачке изменений, в том числе во время разбора HTML.
    // Наблюдатель считает пересечения сам при отрисовке кадра.
    function refresh(entries, pass = measurements()) {
        if (entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) inView.add(entry.target);
                else inView.delete(entry.target);
                if (entry.boundingClientRect && entry.boundingClientRect.width > 0) widths.set(entry.target, entry.boundingClientRect.width);
            });
        }
        if (stopped || !pending.length) return;
        const intersecting = entries && new Set(entries.filter(entry => entry.isIntersecting).map(entry => entry.target));
        const previews = new Map();
        const ready = [];
        pending = pending.filter(image => {
            const attribute = image.hasAttribute(PHOTO) ? PHOTO : SLIDE;
            let keep = doc.documentElement.contains(image) && image.hasAttribute(attribute);
            const intersects = intersecting && intersecting.has(image);
            if (keep && entries && !intersects) return true;
            if (keep && !entries && observer && !inView.has(image)) return true;
            const preview = image.closest('#slider-preview');
            if (preview && !previews.has(preview)) previews.set(preview, preview.querySelector('img'));
            const firstPreview = !preview || previews.get(preview) === image;
            if (keep && firstPreview && !pass.covered(image) && pass.displayed(image) &&
                (!observer || intersects || inView.has(image))) {
                ready.push({ image, attribute });
            }
            if (!keep && observer) observer.unobserve(image);
            return keep;
        });
        // Read every box before changing src/attributes. Alternating those
        // writes with the next image's geometry can force a layout per photo.
        const loaded = new Set();
        ready.forEach(({ image, attribute }) => {
            if (load(image, attribute)) {
                loaded.add(image);
                if (observer) observer.unobserve(image);
            }
        });
        if (loaded.size) pending = pending.filter(image => !loaded.has(image));
    }

    function remember(image) {
        if (inSlider(image) || pending.indexOf(image) !== -1) return;
        pending.push(image);
        if (observer) observer.observe(image);
    }

    function discover(root) {
        if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;
        if (root.nodeType === 1 && root.matches(SELECTOR)) remember(root);
        if (!root.firstElementChild) return;
        const found = root.querySelectorAll(SELECTOR);
        for (let i = 0; i < found.length; i++) remember(found[i]);
    }

    function checkViewport() {
        if (!stopped) refresh(null);
    }

    function pruneImages() {
        pending = pending.filter(image => {
            const keep = doc.documentElement.contains(image) && (image.hasAttribute(PHOTO) || image.hasAttribute(SLIDE));
            if (!keep && observer) observer.unobserve(image);
            return keep;
        });
    }

    // Stylesheets can arrive after this async entry and change hidden/mobile blocks.
    function assetLoaded(event) {
        if (event.target.nodeName === 'LINK') checkViewport();
    }

    function dispose() {
        if (stopped) return;
        stopped = true;
        if (observer) observer.disconnect();
        if (mutations) mutations.disconnect();
        doc.removeEventListener('load', assetLoaded, true);
        doc.removeEventListener('DOMContentLoaded', checkViewport);
        win.removeEventListener('scroll', checkViewport, true);
        win.removeEventListener('resize', checkViewport);
        pending = [];
        if (doc[INSTALLATION] === dispose) delete doc[INSTALLATION];
    }

    doc[INSTALLATION] = dispose;
    if (win.IntersectionObserver) observer = new win.IntersectionObserver(entries => refresh(entries), { rootMargin: MARGIN + 'px' });
    if (win.MutationObserver) {
        let observedBanner = doc.getElementById('hideAppsFullSizeBanner');
        let styleSheetCount = doc.styleSheets.length;
        mutations = new win.MutationObserver(records => {
            let changed = false;
            const roots = new Set();
            const attributes = new Set();
            const banner = doc.getElementById('hideAppsFullSizeBanner');
            let bannerChanged = banner !== observedBanner;
            observedBanner = banner;
            if (banner && doc.styleSheets.length !== styleSheetCount) bannerChanged = true;
            styleSheetCount = doc.styleSheets.length;
            records.forEach(record => {
                if (record.type === 'childList') {
                    for (let i = 0; i < record.addedNodes.length; i++) roots.add(record.addedNodes[i]);
                    if (banner && !bannerChanged) {
                        bannerChanged = banner.contains(record.target) || record.target.nodeName === 'STYLE' ||
                            Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(node => node.contains(banner));
                    }
                    changed = true;
                } else {
                    // Removing a deferred URL is our own completed load. It
                    // cannot reveal another image and needs no layout pass.
                    if ((record.attributeName === PHOTO || record.attributeName === SLIDE) &&
                        !record.target.hasAttribute(record.attributeName)) return;
                    attributes.add(record.target);
                }
            });
            attributes.forEach(target => {
                if (target.matches(SELECTOR)) remember(target);
                if (banner && target.contains(banner)) {
                    bannerChanged = true;
                    changed = true;
                }
                if (pending.some(image => target.contains(image))) changed = true;
            });
            roots.forEach(root => {
                for (let parent = root.parentNode; parent; parent = parent.parentNode) {
                    if (roots.has(parent)) return;
                }
                discover(root);
            });
            // Detached nodes do not need geometry or an active observation.
            pruneImages();
            if (changed || bannerChanged) checkViewport();
        });
        mutations.observe(doc.documentElement, {
            childList: true, subtree: true, attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'id', PHOTO, SLIDE]
        });
    }
    doc.addEventListener('load', assetLoaded, true);
    doc.addEventListener('DOMContentLoaded', checkViewport);
    if (!observer) win.addEventListener('scroll', checkViewport, true);
    win.addEventListener('resize', checkViewport);
    discover(doc);
    checkViewport();
    return dispose;
}
